import { createSelector } from "@reduxjs/toolkit";

import { configReferencesNodeIdCreate } from "../../shared/index.ts";
import { CONDITION_ROOT_SLOT_ID } from "../constants.ts";
import {
  conditionsStateSelector,
  effectsStateSelector,
  responsesByIdSelector,
  triggerIdsStateSelector,
  triggersByIdSelector,
} from "../selectors/triggerSelectors.ts";

export interface NodeAssociation {
  type: "condition" | "effect";
  id: number;
  nodeId: number;
  response: {
    id: number;
    label: string;
    eventTypes: string[]; // all event types from trigger cases
  };
}

export const resolveNodeAssociationsSelector = createSelector(
  [responsesByIdSelector, triggersByIdSelector, triggerIdsStateSelector, conditionsStateSelector, effectsStateSelector],
  (responsesById, triggersById, ids, conditionsState, effectsState) =>
    resolveNodeAssocationsCreate({ responsesById, triggersById, ids, conditionsState, effectsState }),
);

interface Options {
  responsesById: ReturnType<typeof responsesByIdSelector>;
  triggersById: ReturnType<typeof triggersByIdSelector>;
  ids: ReturnType<typeof triggerIdsStateSelector>;
  conditionsState: ReturnType<typeof conditionsStateSelector>;
  effectsState: ReturnType<typeof effectsStateSelector>;
}

export const resolveNodeAssocationsCreate = ({
  responsesById,
  triggersById,
  ids: { handlerIdByResponseId, triggerIdByHandlerId, nodeIdByTriggerId },
  conditionsState,
  effectsState: { byId: effectsById, responseIdByEffectId },
}: Options) => {
  const { byId: conditionsById, responseIdByRootConditionId, parentIdByChildId } = conditionsState;
  const conditionReferencesNodeId = configReferencesNodeIdCreate(conditionsById);
  const effectReferencesNodeId = configReferencesNodeIdCreate(effectsById);

  return (searchNodeId: number): NodeAssociation[] => {
    const buildAssociationContext = (responseId: number): Pick<NodeAssociation, "nodeId" | "response"> | undefined => {
      const handlerId = handlerIdByResponseId[responseId];
      if (!handlerId) {
        throw new Error(`Invalid data; response with id ${responseId} has no handler.`);
      }
      const triggerId = triggerIdByHandlerId[handlerId];
      if (!triggerId) {
        throw new Error(`Invalid data; handler with id ${handlerId} has no trigger.`);
      }

      const nodeId = nodeIdByTriggerId[triggerId];
      if (nodeId === searchNodeId) {
        return undefined;
      }
      const trigger = triggersById[triggerId];
      if (!trigger) {
        return undefined;
      }
      const eventTypes = trigger.cases.map((c) => c.eventType);
      const response: NodeAssociation["response"] = {
        id: responseId,
        eventTypes,
        label: responsesById[responseId]?.label ?? "",
      };
      return {
        nodeId,
        response,
      };
    };

    const conditions = Object.entries(conditionsById)
      .filter(
        ([conditionId, { nodeId }]) => nodeId === searchNodeId || conditionReferencesNodeId(conditionId, searchNodeId),
      )
      .map(([conditionIdStr]): NodeAssociation | undefined => {
        const conditionId = Number(conditionIdStr);
        const findResponseIdByConditionId = (id: number): number => {
          if (id in parentIdByChildId && parentIdByChildId[id] !== CONDITION_ROOT_SLOT_ID.parentId) {
            return findResponseIdByConditionId(parentIdByChildId[id]);
          }
          return responseIdByRootConditionId[id];
        };

        const responseId = findResponseIdByConditionId(conditionId);
        if (!responseId) return undefined;
        const context = buildAssociationContext(responseId);
        if (!context) return undefined;
        return {
          ...context,
          id: conditionId,
          type: "condition",
        };
      });

    const effects = Object.entries(effectsById)
      .filter(
        ([effectId, { nodeId, targetNodeId }]) =>
          (nodeId === searchNodeId && targetNodeId === undefined) ||
          targetNodeId === searchNodeId ||
          effectReferencesNodeId(effectId, searchNodeId),
      )
      .map(([effectIdStr]): NodeAssociation | undefined => {
        const effectId = Number(effectIdStr);
        const responseId = responseIdByEffectId[effectId];
        if (!responseId) return undefined;
        const context = buildAssociationContext(responseId);
        if (!context) return undefined;
        return {
          ...context,
          id: effectId,
          type: "effect",
        };
      });

    return [...conditions, ...effects].filter((a): a is NonNullable<typeof a> => !!a);
  };
};

export type ResolveNodeAssociations = ReturnType<typeof resolveNodeAssocationsCreate>;
